import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

function releaseText(ownership) {
  const release = ownership.release;
  if (!release) return '—';
  return ownership.ext_release ? `${release} / ${ownership.ext_release}` : release;
}

function packagePathText(ownership) {
  const path = Array.isArray(ownership.package_path) ? ownership.package_path : [];
  if (!path.length) return ownership.package || '—';
  return path.map((p) => p.package).join(' → ');
}

function contactText(contact) {
  if (!contact) return '';
  const parts = [];
  if (contact.assignment_group) parts.push(`group ${contact.assignment_group}`);
  if (contact.email) parts.push(contact.email);
  return parts.join(' · ');
}

export default function SapJobOwnershipPanel({ ownership }) {
  if (!ownership) return null;
  const status = ownership.status;
  return (
    <Card data-testid="sap-job-ownership">
      <CardHeader>
        <CardTitle>Ownership analysis</CardTitle>
        <CardDescription>
          Where the job&apos;s program sits in SAP. Standard SAP programs map to an SAP support area,
          not a customer department.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3 text-sm">
        {ownership.contact ? (
          <div className="rounded-md border p-3" data-testid="ownership-contact">
            <span className="font-medium">Who to notify: </span>
            {contactText(ownership.contact) ? (
              <>
                {contactText(ownership.contact)}
                <span className="text-muted-foreground">
                  {ownership.contact.matched_by === 'default'
                    ? ' (default owner — no mapping for this owner)'
                    : ' (from the owner mapping)'}
                </span>
              </>
            ) : (
              <span className="text-muted-foreground">
                nobody — no mapping for this owner and no default owner is set (Settings → Job owners)
              </span>
            )}
          </div>
        ) : null}
        {status === 'resolved' ? (
          <div className="grid gap-2 sm:grid-cols-2">
            <div>
              Application component:{' '}
              <span className="font-mono" data-testid="ownership-component">
                {ownership.application_component || '—'}
              </span>
            </div>
            <div>
              Component text (owner):{' '}
              <span data-testid="ownership-owner">{ownership.component_text || '—'}</span>
            </div>
            <div>
              Classification:{' '}
              <span data-testid="ownership-classification">
                {ownership.classification_text
                  ? `${ownership.classification_text}${
                      ownership.classification_code ? ` (${ownership.classification_code})` : ''
                    }`
                  : '—'}
              </span>
            </div>
            <div>
              Package: <span className="font-mono">{ownership.package || '—'}</span>
              {ownership.package_text ? ` (${ownership.package_text})` : ''}
            </div>
            <div>Package path: <span className="font-mono">{packagePathText(ownership)}</span></div>
            <div>Origin: {ownership.origin || '—'}</div>
            <div>Author: {ownership.author || '—'}</div>
            <div>Delivery unit: <span className="font-mono">{ownership.delivery_unit || '—'}</span></div>
            <div>Release: {releaseText(ownership)}</div>
            <div>Person responsible: {ownership.person_responsible || '—'}</div>
            <div>Program: <span className="font-mono">{ownership.program || '—'}</span></div>
          </div>
        ) : (
          <p className="text-muted-foreground" data-testid="ownership-unresolved">
            {status === 'not_found' && 'Program not found in the SAP repository (it may be an include or external command).'}
            {status === 'no_program' && 'No program is recorded for this run.'}
            {status === 'unavailable' && 'Ownership lookup was unavailable when this run was processed.'}
            {!['not_found', 'no_program', 'unavailable'].includes(status) && 'Ownership not resolved.'}
          </p>
        )}
      </CardContent>
    </Card>
  );
}
